import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { api, formatApiError } from "../lib/api";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function Companies() {
  const t = useT();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "" });

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/companies");
      setItems(data || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  function openCreate() {
    setEditing(null);
    setForm({ name: "", description: "" });
    setShowForm(true);
  }
  function openEdit(c) {
    setEditing(c);
    setForm({ name: c.name, description: c.description || "" });
    setShowForm(true);
  }

  async function save() {
    if (!form.name) return toast.error("Name is required");
    try {
      if (editing) await api.patch(`/companies/${editing.id}`, form);
      else await api.post("/companies", form);
      toast.success("Saved");
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete company and all its data?")) return;
    try {
      await api.delete(`/companies/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="kpi-label">{t("nav.companies")}</p>
          <h1 className="text-2xl font-bold tracking-tight">{t("company.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("company.subtitle")}</p>
        </div>
        <Button onClick={openCreate} data-testid="new-company-btn">
          <Plus className="h-4 w-4 mr-1.5" /> {t("company.new")}
        </Button>
      </div>

      <Card className="card-soft">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("common.name")}</TableHead>
                <TableHead>{t("common.description")}</TableHead>
                <TableHead>{t("common.created")}</TableHead>
                <TableHead className="text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">{t("common.loading")}</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                    <Building2 className="h-5 w-5 mx-auto mb-2" />
                    {t("company.no_companies")}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((c) => (
                  <TableRow key={c.id} data-testid={`company-row-${c.id}`}>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.description || "—"}</TableCell>
                    <TableCell className="text-xs">{new Date(c.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(c)} data-testid={`edit-company-${c.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(c.id)} data-testid={`delete-company-${c.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent data-testid="company-form-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? t("company.edit") : t("company.new")}</DialogTitle>
            <DialogDescription>{t("company.dialog_subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="cn">{t("common.name")}</Label>
              <Input id="cn" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cd">{t("common.description")}</Label>
              <Textarea id="cd" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>{t("common.cancel")}</Button>
            <Button onClick={save} data-testid="company-save">{editing ? t("common.save") : t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
